import { Link } from 'react-router';

import { useSessionStore } from '@shared/auth/session';
import { canReach } from '@shared/chrome/nav';

import { copy } from './copy';
import { ReportSection } from './Index.ReportSection';
import { REPORT_SECTIONS } from './Index.reports';

/**
 * The console entry — the four reporting dashboards, each a row of figures over
 * a breakdown table, each with the route to the surface that acts on it.
 *
 * **Four independent reads, never one composite.** One unreachable peer takes
 * its own band down and leaves the other three usable.
 *
 * What this page deliberately does not draw, and why: no chart, because the
 * design system names no chart shape and `backend-analytics` ships no chart band
 * on the wire; no period selector, because no catalog row gives a reporting
 * surface a period and borrowing the search panel's date facet would be
 * inventing configuration; no row action of any kind, because a report that
 * could act would put a decision on a surface whose row does not carry one.
 *
 * There is no page-level billboard here — this area draws none — so the text
 * head is the page's entry.
 */
export function IndexPage() {
  return (
    <>
      <div className="mb-6 mt-5 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        {/*
          The lede states two facts the operator needs — what they are reading,
          and that it is a snapshot rather than a stream. It does not state that
          the page has links: a link is seen.
        */}
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {REPORT_SECTIONS.map((spec) => (
        <ReportSection
          key={spec.id}
          spec={spec}
          beforeTable={spec.id === 'engagement' ? <QueueLinks /> : undefined}
          afterTable={sectionNote(spec.id)}
        />
      ))}
    </>
  );
}

/** The line beneath a section's table, where the section carries one. */
function sectionNote(id: string) {
  if (id === 'content') return copy.notes.content;
  if (id === 'engagement') return copy.notes.engagement;
  if (id === 'revenue') return <RevenueNote />;
  return undefined;
}

/**
 * The gateway registry is `admin` in finance, and the revenue section is
 * `operator` in analytics — so an operator can read this table and not the
 * surface the line points at. The sentence stands on its own without the link;
 * only the link goes.
 */
function RevenueNote() {
  const capabilities = useSessionStore((state) => state.capabilities);

  if (!canReach(capabilities, '/payment-gateways')) return <>{copy.notes.revenuePrefix}</>;

  return (
    <>
      {copy.notes.revenuePrefix}{' '}
      <Link to="/payment-gateways" className="text-accent-text">
        {copy.notes.revenueLink}
      </Link>
    </>
  );
}

/**
 * The two queues an operator works, on the section that reports their depth.
 * Every committing action inside the content column is secondary — the top bar's
 * create action is this view's one primary button.
 *
 * Equal columns from `--bp-sm` up, so the strip spans the same measure as the
 * figures above it and the table below it. Left to intrinsic width it hugs the
 * inline start and reads as chips orphaned in the empty half of the column — a
 * band the eye has to re-find between two full-width blocks.
 *
 * The base tier stays a wrapping row: columns at 320 px give each label less
 * width than the label needs, and a wrapped word inside a control is worse than
 * a wrapped row of controls.
 */
function QueueLinks() {
  // Two owning services, two queues: comments is engagement's and cases is
  // support's. An account holding one and not the other draws the strip it can
  // work — a link the guard bounces looks like a working destination and teaches
  // the operator nothing when it lands them back here.
  //
  // «افتح البلاغات» left with `/reports`: that queue was the case queue filtered
  // to one type, so the strip offered two links to one surface.
  const capabilities = useSessionStore((state) => state.capabilities);
  const links = [
    { to: '/comments', label: copy.queueLinks.comments },
    { to: '/cases', label: copy.queueLinks.cases },
  ].filter((link) => canReach(capabilities, link.to));

  if (links.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 sm:grid sm:grid-cols-2">
      {links.map((link) => (
        <QueueLink key={link.to} to={link.to} label={link.label} />
      ))}
    </div>
  );
}

function QueueLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="hit-target flex items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-3 text-center text-xs font-semibold text-ink no-underline hover:bg-surface-overlay"
    >
      {label}
    </Link>
  );
}
